import React, { useState, useEffect } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';

import Select from '../../components/ui/Select';
import EscalationKPICard from './components/EscalationKPICard';
import EscalationVolumeChart from './components/EscalationVolumeChart';
import EscalationFunnelChart from './components/EscalationFunnelChart';
import LiveEscalationQueue from './components/LiveEscalationQueue';
import EscalationTable from './components/EscalationTable';
import EscalationHeatMap from './components/EscalationHeatMap';
import EmailHistoryTable from './components/EmailHistoryTable';
import EmailThreadPane from './components/EmailThreadPane';
import Input from '../../components/ui/Input';


const EscalationsMonitoring = () => {
  const [escalationType, setEscalationType] = useState('all');
  const [urgencyLevel, setUrgencyLevel] = useState('all');
  const [assignmentStatus, setAssignmentStatus] = useState('all');

  // Email management states
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  const [emailDateFilter, setEmailDateFilter] = useState('all-time');
  const [emailSortBy, setEmailSortBy] = useState('newest');
  const [emailCategory, setEmailCategory] = useState('All Emails');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isEmailPaneOpen, setIsEmailPaneOpen] = useState(false);

  const escalationTypeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'technical', label: 'Technical Issues' },
  { value: 'billing', label: 'Billing Disputes' },
  { value: 'complex', label: 'Complex Inquiries' },
  { value: 'escalation', label: 'Customer Requests' }];


  const urgencyLevelOptions = [
  { value: 'all', label: 'All Urgency Levels' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }];


  const assignmentStatusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'pending', label: 'Pending Assignment' },
  { value: 'waiting', label: 'Waiting Response' }];

  // Email filter options
  const emailDateFilterOptions = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last-7-days', label: 'Last 7 Days' },
  { value: 'last-14-days', label: 'Last 14 Days' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' },
  { value: 'all-time', label: 'All Time' }];


  const emailSortOptions = [
  { value: 'newest', label: 'Date (Newest)' },
  { value: 'oldest', label: 'Date (Oldest)' }];


  const emailCategoryOptions = [
  'All Emails',
  'New - Informational',
  'New - Action Required',
  'Ongoing Thread - Informational',
  'Ongoing Thread - Action Required'];


  const handleEmailRowClick = (email) => {
    setSelectedEmail(email);
    setIsEmailPaneOpen(true);
  };

  const handleCloseEmailPane = () => {
    setIsEmailPaneOpen(false);
    setSelectedEmail(null);
  };

  const kpiData = [
  {
    title: "Escalation Rate",
    value: "12.4%",
    subtitle: "of total interactions",
    trend: "down",
    trendValue: "-2.3%",
    icon: "AlertTriangle",
    iconColor: "#DC2626",
    iconBg: "bg-error/10"
  },
  {
    title: "Avg Resolution Time",
    value: "18.5m",
    subtitle: "post-handover",
    trend: "down",
    trendValue: "-4.2m",
    icon: "Clock",
    iconColor: "#F59E0B",
    iconBg: "bg-warning/10"
  },
  {
    title: "CSAT Impact",
    value: "4.2/5.0",
    subtitle: "escalated cases",
    trend: "up",
    trendValue: "+0.3",
    icon: "Star",
    iconColor: "#059669",
    iconBg: "bg-success/10"
  },
  {
    title: "Cost Differential",
    value: "$8.40",
    subtitle: "AI vs Human",
    trend: "neutral",
    trendValue: "$0.00",
    icon: "DollarSign",
    iconColor: "#3B82F6",
    iconBg: "bg-primary/10"
  }];


  const volumeChartData = [
  { date: "Dec 23", technical: 45, billing: 32, complex: 28 },
  { date: "Dec 24", technical: 52, billing: 38, complex: 31 },
  { date: "Dec 25", technical: 38, billing: 25, complex: 22 },
  { date: "Dec 26", technical: 58, billing: 42, complex: 35 },
  { date: "Dec 27", technical: 62, billing: 48, complex: 38 },
  { date: "Dec 28", technical: 55, billing: 40, complex: 33 },
  { date: "Dec 29", technical: 48, billing: 35, complex: 29 },
  { date: "Dec 30", technical: 51, billing: 37, complex: 31 }];


  const funnelData = [
  { stage: "AI Limitation", count: 1247, avgTime: "Instant" },
  { stage: "Queue Assignment", count: 1189, avgTime: "2.3 min" },
  { stage: "Agent Pickup", count: 1142, avgTime: "4.7 min" },
  { stage: "Resolution", count: 1098, avgTime: "18.5 min" }];


  const liveQueueData = [
  {
    id: 1,
    customerName: "Priya Mehta",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b43e8b7f-1763295504724.png",
    customerAvatarAlt: "Professional headshot of woman with shoulder-length brown hair wearing navy blazer and white blouse",
    reason: "Wrong socks delivered, wants immediate replacement",
    priority: "Critical",
    status: "Assigned",
    assignedAgent: "Rohit Sharma",
    slaMinutes: 8,
    waitTime: "3m 24s"
  },
  {
    id: 2,
    customerName: "Arjun Nair",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb8988be-1763295050652.png",
    customerAvatarAlt: "Professional headshot of man with short black hair wearing gray suit and blue tie",
    reason: "Refund not processed for returned Neon Skull Combo Pack",
    priority: "High",
    status: "In Progress",
    assignedAgent: "Sneha Iyer",
    slaMinutes: 22,
    waitTime: "8m 15s"
  },
  {
    id: 3,
    customerName: "Pooja Deshmukh",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16534c52e-1763295643551.png",
    customerAvatarAlt: "Professional headshot of woman with long dark hair wearing burgundy blazer",
    reason: "Bulk order inquiry for corporate gifting",
    priority: "Medium",
    status: "Pending",
    assignedAgent: null,
    slaMinutes: 45,
    waitTime: "1m 42s"
  },
  {
    id: 4,
    customerName: "Karan Malhotra",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1137e7a04-1763296927252.png",
    customerAvatarAlt: "Professional headshot of man with short hair wearing charcoal suit and striped tie",
    reason: "Damaged packaging, socks received with stains",
    priority: "High",
    status: "Assigned",
    assignedAgent: "Anjali Rao",
    slaMinutes: 18,
    waitTime: "5m 33s"
  },
  {
    id: 5,
    customerName: "Sunita Chopra",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11176ce80-1763296378826.png",
    customerAvatarAlt: "Professional headshot of woman with curly hair wearing teal blouse and pearl necklace",
    reason: "Size exchange needed for Specticals Charm Crew Socks",
    priority: "Medium",
    status: "Waiting",
    assignedAgent: "Vivek Jain",
    slaMinutes: 38,
    waitTime: "12m 08s"
  }];


  const escalationHistoryData = [
  {
    id: 1,
    caseId: "ESC-2024-1247",
    customerName: "Rahul Khanna",
    customerEmail: "rahul.khanna@gmail.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1be218dcc-1763296572196.png",
    customerAvatarAlt: "Professional headshot of man with short brown hair wearing navy suit and red tie",
    reason: "Wrong Item - Received Dazzler Dark instead of Shadow Spectrum",
    customerTier: "Premium",
    assignedAgent: "Meera Iyer",
    resolutionTime: "15m 32s",
    outcome: "Resolved"
  },
  {
    id: 2,
    caseId: "ESC-2024-1246",
    customerName: "Divya Menon",
    customerEmail: "divya.menon@yahoo.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f4c07e6c-1763297701460.png",
    customerAvatarAlt: "Professional headshot of woman with long dark hair wearing white blouse and black blazer",
    reason: "Payment Dispute - Double charged for Neon Skull Combo Pack",
    customerTier: "Standard",
    assignedAgent: "Arun Kapoor",
    resolutionTime: "22m 18s",
    outcome: "Resolved"
  },
  {
    id: 3,
    caseId: "ESC-2024-1245",
    customerName: "Sanjay Mishra",
    customerEmail: "s.mishra@outlook.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_103972665-1763296698542.png",
    customerAvatarAlt: "Professional headshot of man with short black hair wearing gray suit and blue shirt",
    reason: "Bulk Order - Corporate gifting 200 pairs of crew socks",
    customerTier: "Premium",
    assignedAgent: "Nisha Pillai",
    resolutionTime: "28m 45s",
    outcome: "Pending"
  },
  {
    id: 4,
    caseId: "ESC-2024-1244",
    customerName: "Ritu Saxena",
    customerEmail: "ritu.saxena@gmail.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1631c1677-1763295642190.png",
    customerAvatarAlt: "Professional headshot of woman with short dark hair wearing burgundy blazer and pearl earrings",
    reason: "Size Exchange - Specticals Charm Crew Socks too small",
    customerTier: "Standard",
    assignedAgent: "Alok Tiwari",
    resolutionTime: "12m 55s",
    outcome: "Resolved"
  },
  {
    id: 5,
    caseId: "ESC-2024-1243",
    customerName: "Manoj Pandey",
    customerEmail: "m.pandey@gmail.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e5173305-1763295835079.png",
    customerAvatarAlt: "Professional headshot of man with short gray hair wearing charcoal suit and striped tie",
    reason: "Delivery Issue - Order stuck in transit for 7 days",
    customerTier: "Basic",
    assignedAgent: "Geeta Krishnan",
    resolutionTime: "19m 33s",
    outcome: "Re-escalated"
  },
  {
    id: 6,
    caseId: "ESC-2024-1242",
    customerName: "Swati Bhatt",
    customerEmail: "s.bhatt@gmail.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_103c16fd1-1763300797899.png",
    customerAvatarAlt: "Professional headshot of woman with long dark hair wearing navy blazer and white blouse",
    reason: "Refund Delay - Return accepted but refund pending 15 days",
    customerTier: "Premium",
    assignedAgent: "Nitin Desai",
    resolutionTime: "16m 42s",
    outcome: "Resolved"
  },
  {
    id: 7,
    caseId: "ESC-2024-1241",
    customerName: "Ajay Srivastava",
    customerEmail: "a.srivastava@yahoo.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1137e7a04-1763296927252.png",
    customerAvatarAlt: "Professional headshot of man with short hair wearing light gray suit and blue tie",
    reason: "Quality Issue - Color fading on Paradise Trip Crew Socks",
    customerTier: "Standard",
    assignedAgent: "Prachi Joshi",
    resolutionTime: "31m 15s",
    outcome: "Pending"
  },
  {
    id: 8,
    caseId: "ESC-2024-1240",
    customerName: "Lakshmi Rao",
    customerEmail: "l.rao@gmail.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1194a8e94-1763301813569.png",
    customerAvatarAlt: "Professional headshot of woman with curly brown hair wearing teal blazer and gold necklace",
    reason: "Damaged Product - Running Ankle Socks torn on arrival",
    customerTier: "Basic",
    assignedAgent: "Harish Gupta",
    resolutionTime: "8m 27s",
    outcome: "Resolved"
  },
  {
    id: 9,
    caseId: "ESC-2024-1239",
    customerName: "Gaurav Thakur",
    customerEmail: "g.thakur@outlook.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1be218dcc-1763296572196.png",
    customerAvatarAlt: "Professional headshot of man with short dark hair wearing navy suit and red patterned tie",
    reason: "Order Cancellation - Wants to cancel before shipping",
    customerTier: "Standard",
    assignedAgent: "Shweta Kulkarni",
    resolutionTime: "14m 08s",
    outcome: "Resolved"
  },
  {
    id: 10,
    caseId: "ESC-2024-1238",
    customerName: "Rekha Banerjee",
    customerEmail: "r.banerjee@gmail.com",
    customerAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9d00cbc-1763296697107.png",
    customerAvatarAlt: "Professional headshot of woman with shoulder-length dark hair wearing black blazer and silver jewelry",
    reason: "Missing Item - Love Drip K-Edition missing from combo order",
    customerTier: "Premium",
    assignedAgent: "Dinesh Chauhan",
    resolutionTime: "20m 52s",
    outcome: "Resolved"
  }];


  const heatMapData = [
  [15, 12, 18, 45, 72, 68, 52, 38],
  [18, 14, 22, 52, 78, 75, 58, 42],
  [22, 16, 28, 58, 85, 82, 65, 48],
  [20, 15, 25, 55, 80, 78, 62, 45],
  [25, 18, 32, 62, 88, 85, 68, 52],
  [12, 10, 15, 35, 55, 48, 38, 28],
  [8, 6, 10, 22, 38, 32, 25, 18]];


  return (
    <>
      <PrimaryNavigation />
      <div className="main-content">
        <div className="min-h-screen bg-background">
          <NavigationBreadcrumb
            items={[
            { label: 'Dashboard', href: '/' },
            { label: 'Escalations Monitoring', href: '/escalations-monitoring' }]
            } />

          <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 md:mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">Escalations Monitoring</h1>
                <p className="text-sm md:text-base text-muted-foreground">Track and manage AI-to-human escalations in real-time</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 md:mb-8">
              





              <Select
                options={urgencyLevelOptions}
                value={urgencyLevel}
                onChange={setUrgencyLevel}
                placeholder="Select urgency level" />

              <Select
                options={assignmentStatusOptions}
                value={assignmentStatus}
                onChange={setAssignmentStatus}
                placeholder="Select assignment status" />

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              {kpiData?.map((kpi, index) =>
              <EscalationKPICard key={index} {...kpi} />
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
              <EscalationVolumeChart data={volumeChartData} />
              <EscalationFunnelChart data={funnelData} />
            </div>

            <div className="mb-6 md:mb-8">
              <LiveEscalationQueue data={liveQueueData} />
            </div>

            <div className="mb-6 md:mb-8">
              <EscalationHeatMap />
            </div>

            <div className="mb-6 md:mb-8">
              <EscalationTable escalations={escalationHistoryData} />
            </div>

            {/* Email Management Section */}
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6 md:mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4 md:mb-6">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Email Management</h2>
                  <p className="text-xs md:text-sm text-muted-foreground">Monitor and manage customer email communications</p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                  <Select
                    options={emailSortOptions}
                    value={emailSortBy}
                    onChange={setEmailSortBy}
                    className="w-full sm:w-40" />

                  <Select
                    options={emailDateFilterOptions}
                    value={emailDateFilter}
                    onChange={setEmailDateFilter}
                    className="w-full sm:w-44" />

                </div>
              </div>

              <div className="mb-4">
                <Input
                  type="search"
                  placeholder="Search emails by customer name, ID, or concern..."
                  value={emailSearchTerm}
                  onChange={(e) => setEmailSearchTerm(e?.target?.value)}
                  className="w-full" />

              </div>

              <div className="mb-4">
                <p className="text-xs text-muted-foreground mb-2">Email Category Filter</p>
                <div className="flex flex-wrap gap-2">
                  {emailCategoryOptions?.map((category) =>
                  <button
                    key={category}
                    onClick={() => setEmailCategory(category)}
                    className={`px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-base ${
                    emailCategory === category ?
                    'bg-primary text-primary-foreground' :
                    'bg-muted text-muted-foreground hover:bg-muted/80'}`
                    }>

                      {category}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <EmailHistoryTable
              onRowClick={handleEmailRowClick}
              searchTerm={emailSearchTerm}
              dateFilter={emailDateFilter}
              sortBy={emailSortBy}
              emailCategory={emailCategory} />


            <EmailThreadPane
              isOpen={isEmailPaneOpen}
              onClose={handleCloseEmailPane}
              emailData={selectedEmail} />

          </main>
        </div>
      </div>
    </>);

};

export default EscalationsMonitoring;